// Unique number -> "Location/Room/Device", for ANY device on the broker.
//
// WHY THIS EXISTS
// The console watches '#', so it sees traffic from every device Smartify has
// deployed — not just the ones belonging to the signed-in user. Naming them from
// `getDevicesByUser` therefore labels a handful and calls the rest "Unknown
// device", which is exactly backwards for a diagnostic tool: the numbers you
// most need identified are the ones that aren't yours.
//
// `GetDeviceDetailbyUniqueNumber` answers for any unique number regardless of
// ownership, so it is the authority here. The user's own device list is still
// used, as a free prime — it is already loaded and saves a request per device.
//
// COST CONTROL. One request per unique number, ever:
//   • results are cached, INCLUDING misses (a number that resolves to nothing is
//     never asked about twice)
//   • lookups run one at a time through a queue with a gap between them, so a
//     burst of unfamiliar numbers in the log trickles rather than floods
//   • a hard ceiling of MAX_LOOKUPS per session, after which unknown numbers
//     simply stay unlabelled — a console that DoSes the API to draw captions has
//     its priorities wrong
//
// Callers read synchronously (`getLabel`) and subscribe for repaints; nothing
// renders behind an await.

import { getDeviceDetail } from '../api/api';
import { deviceCatalogEntry } from './deviceCatalog';
import { matchesTokens } from '../utils/search';

// Ceiling on server lookups per session. Generous enough to name everything on a
// normal broker, low enough to bound the damage if '#' is unexpectedly busy.
export const MAX_LOOKUPS = 300;

// Gap between queued lookups.
const STEP_MS = 150;

// How often subscribers are told about newly resolved labels. Coalesced for the
// same reason the message buffer coalesces: a resolved label repaints a 500-row
// list, and doing that per lookup makes the console stutter.
const FLUSH_MS = 250;

// uno -> label. An empty string means "asked, no such device" and is a real
// cached answer, not a miss — that is why lookups test `has`, not truthiness.
const cache = new Map();
// uno -> DeviceID, so a search can also find a device by its TYPE name.
const typeIds = new Map();

const queue = [];
const queued = new Set();
let lookups = 0;
let running = false;

const listeners = new Set();
let flushTimer = null;

function notify() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    listeners.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
  }, FLUSH_MS);
}

// The one place the caption's shape is decided.
//
//   Sanjay Ailani - Home/Parking/Ac Touch Switch
//   owner         - loc /room   /device
//
// The owner leads because this console sees the whole broker: "Home/Parking"
// is ambiguous across customers and only the owner's name disentangles them.
// ⚠️ That goes for YOUR OWN devices too. A Super Admin reading the log cannot
// tell whose "Home" just switched — theirs, or one of the 330 other homes — so
// there is no short three-part form any more; a caption without an owner is a
// caption still being resolved (see PRIMED below).
//
// The owner is the FULL name. Probed on the live API (2026-09-19,
// GetDeviceDetailbyUniqueNumber for 910600):
//
//   DeviceMasterUser      "Ailani Sanjay Hareshlal"   <- full, use this
//   DeviceMasterUserName  "Sanjay"                    <- given name ONLY
//
// The names invert what you would guess, and the given name alone identifies
// nobody on a broker with many Sanjays. The older backend in the repo builds
// the same string as `FullName` (CONCAT of Surname, Name, FatherHusband), so
// that is accepted too, then the triple assembled from its pieces, and the bare
// given name only as a last resort. The user's own device list carries none of
// these. The device's INSTALLED name is `ActualDeviceName` on that list and
// `DeviceName` on the detail row (where `DeviceName` on the list is the TYPE's
// name) — hence the order below.
//
// Owner and path are separated by " - " so the two read as two facts; the path
// is joined with a bare "/" because this rides on the end of every log line,
// where each character costs width the payload needs more.
export function ownerOfDevice(d) {
  if (!d) return '';
  const raw = d.DeviceMasterUser || d.FullName
    || [d.Surname, d.Name, d.FatherHusband].filter(Boolean).join(' ')
    || d.DeviceMasterUserName || '';
  return String(raw).replace(/\s+/g, ' ').trim();
}

export function pathOfDevice(d) {
  if (!d) return '';
  return [d.LocationName, d.RoomName, d.ActualDeviceName || d.DeviceName]
    .filter(Boolean).join('/');
}

export function labelOfDevice(d) {
  const path = pathOfDevice(d);
  if (!path) return '';
  const owner = ownerOfDevice(d);
  return owner ? `${owner} - ${path}` : path;
}

/**
 * The caption for a DETAIL row — the server's current answer — which, unlike a
 * list row, can say a device is UNASSIGNED (LocationID 0). That is stated, not
 * left as a bare name: "Unassigned - SMARTIFY_803033" reads as a fact, a bare
 * name reads as a caption that has not resolved yet.
 */
export function captionOfRow(d) {
  if (!d) return '';
  const loc = d.LocationID ?? d.LocationId;
  if (loc != null && Number(loc) === 0) {
    const name = d.ActualDeviceName || d.DeviceName || '';
    return name ? `Unassigned - ${name}` : 'Unassigned';
  }
  return labelOfDevice(d);
}

/**
 * A fresh detail row was just read — make the caption match it NOW.
 *
 * ⚠️ CAPTIONS WERE CACHED FOR THE WHOLE SESSION. After 803033 was unassigned
 * the Message Box still read "Ailani Roshan Ramesh - Home/Hall/SMARTIFY_803033"
 * on its breadcrumb and every log line, while the server said owner 0, location
 * 0 (2026-10-06). Anything that re-reads a device row (the console's live
 * refresh, an unassign, an assign) passes it here, and every caption on screen
 * follows.
 */
export function noteDeviceRow(row) {
  const k = key(row && row.UniqueNumber);
  if (!k) return;
  if (row.DeviceID != null) typeIds.set(k, Number(row.DeviceID));
  primed.delete(k);
  const text = captionOfRow(row);
  if (cache.get(k) !== text) {
    cache.set(k, text);
    notify();
  }
}

// PRIMED: numbers whose caption came from a device LIST and so has no owner.
// They read fine immediately, but `requestLabel` still queues them for the one
// detail fetch that adds the owner; `lookup` then overwrites and clears this.
const primed = new Set();

const key = (uno) => String(uno ?? '').trim();

// Seed from a device list already in hand (the user's own devices). Free, and
// covers the common case without a single request.
export function primeLabels(devices) {
  let added = 0;
  (devices || []).forEach((d) => {
    const k = key(d.UniqueNumber);
    const text = labelOfDevice(d);
    // Only fill blanks: a list row is not more authoritative than a detail
    // fetch, and overwriting would make labels flicker between the two shapes.
    if (k && d.DeviceID != null && !typeIds.has(k)) typeIds.set(k, Number(d.DeviceID));
    if (k && text && !cache.get(k)) {
      cache.set(k, text);
      if (!ownerOfDevice(d)) primed.add(k);
      added += 1;
    }
  });
  if (added) notify();
}

// What we know right now. null = never looked up; '' = looked up, not a device.
export function getLabel(uno) {
  const k = key(uno);
  return cache.has(k) ? cache.get(k) : null;
}

/**
 * Everything a log search should find a device by: its caption (owner,
 * location, room, installed name) AND its TYPE name from the DeviceTypes table.
 *
 * ⚠️ THE TYPE NAME IS NOT IN THE CAPTION. A search for "4+1 Switiching System"
 * (sic — that is the table's own spelling, DeviceType 1) found nothing, because
 * the caption carries the INSTALLED name and the search looked only at the raw
 * payload. The type name is the one an installer reads off the box.
 */
export function getSearchText(uno) {
  const k = key(uno);
  const id = typeIds.get(k);
  const type = id != null ? (deviceCatalogEntry(id)?.name || '') : '';
  return [cache.get(k) || '', type].filter(Boolean).join(' ');
}

/**
 * Does a log line match a search? The payload by plain substring (so "1-1" and
 * "STATUS" behave exactly as before), OR the device's caption/type by words.
 *
 * ⚠️ WORDS ONLY WHEN THE QUERY HAS A LETTER. Word matching is prefix/substring
 * per token, so "1-1" against captions would match every device whose name
 * holds a "1" — and drown the payload search it was meant to sharpen.
 */
export function logLineMatches(text, uno, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  if (String(text || '').toLowerCase().includes(q)) return true;
  if (!/[a-z]/i.test(q)) return false;
  const hay = getSearchText(uno);
  return !!hay && matchesTokens(hay, q);
}

async function lookup(k) {
  try {
    const res = await getDeviceDetail(k);
    const rows = Array.isArray(res?.data) ? res.data : [];
    const text = captionOfRow(rows[0]);
    if (rows[0] && rows[0].DeviceID != null) typeIds.set(k, Number(rows[0].DeviceID));
    // A primed caption is kept over an empty detail reply: the list said this
    // IS a device, and "Home/Parking/…" beats nothing.
    if (text || !primed.has(k)) cache.set(k, text);
    primed.delete(k);
  } catch (e) {
    // A failed request is NOT a cached miss — the number may well be a device,
    // the network just said no. Leaving it uncached lets a later mention retry.
    return;
  }
  notify();
}

async function drain() {
  if (running) return;
  running = true;
  while (queue.length && lookups < MAX_LOOKUPS) {
    const k = queue.shift();
    queued.delete(k);
    if (!cache.has(k) || primed.has(k)) {
      lookups += 1;
      // eslint-disable-next-line no-await-in-loop
      await lookup(k);
      // eslint-disable-next-line no-await-in-loop
      if (queue.length) await new Promise((r) => setTimeout(r, STEP_MS));
    }
  }
  running = false;
}

// Ask for a label in the background. Safe to call for every log row on every
// render: already-known and already-queued numbers cost nothing. A PRIMED
// number (owner not yet known) counts as unknown here — one lookup upgrades it.
export function requestLabel(uno) {
  const k = key(uno);
  if (!k || queued.has(k)) return;
  if (cache.has(k) && !primed.has(k)) return;
  if (lookups >= MAX_LOOKUPS) return;
  queued.add(k);
  queue.push(k);
  drain();
}

// Resolve now, ahead of the queue — for the one number the user is actually
// looking at (the command box), which should not wait behind background work.
//
// Does not count against MAX_LOOKUPS: that ceiling exists to bound traffic the
// user did not ask for, and this is a direct response to something they typed.
export async function resolveLabel(uno) {
  const k = key(uno);
  if (!k) return '';
  if (cache.has(k) && !primed.has(k)) return cache.get(k);
  await lookup(k);
  return cache.has(k) ? cache.get(k) : '';
}

export function subscribeLabels(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
